import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw, Cloud, Download, Search, X, Inbox, Check, FileText,
  MoreVertical, Ban, Trash2, RotateCcw, CalendarRange, Loader2,
} from "lucide-react";
import { logger } from "../../utils/logger";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import DailyPullJobsPanel from "./DailyPullJobsPanel";
import AutoSyncCard from "./AutoSyncCard";
import InvoiceDocumentPreview from "../invoices/InvoiceDocumentPreview";
import { useInvoicePreview } from "@/contexts/InvoicePreviewContext";
import { usePageContainer } from "@/hooks/usePageContainer";
import { formatDate as fmtDate, formatMonth as fmtMonth, formatAmount as fmtAmount } from "@/lib/formatters";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pakiet 1 (2026-02-24): lokalne formattery zastąpione centralnymi z `lib/formatters`.
// Aliasy zachowują kompatybilność z resztą pliku — zero zmian w call-site.
const formatMoney = fmtAmount;
const formatDate = fmtDate;
const formatMonth = fmtMonth;

const PAYMENT_FORMS = {
  "1": "Gotówka", "2": "Karta", "3": "Bon", "4": "Czek", "5": "Weksel",
  "6": "Przelew", "7": "Mobilna", "8": "Kredyt/Pożyczka", "9": "Faktoring", "10": "Inna",
};
// Kanoniczne wartości wewnętrzne (angielskie) → polskie etykiety UI.
const PAYMENT_METHOD_LABELS = {
  transfer: "Przelew",
  cash: "Gotówka",
  card: "Karta",
  blik: "BLIK",
  online_payment: "Płatność online",
  online: "Płatność online",
  other_non_transfer: "Inna forma (nie-przelew)",
  mixed: "Mieszane",
  unknown: "Nieokreślona",
};
const formatPaymentMethod = (code) => {
  if (code === null || code === undefined || code === "") return "—";
  const s = String(code).trim();
  if (PAYMENT_FORMS[s]) return PAYMENT_FORMS[s];
  const low = s.toLowerCase();
  if (PAYMENT_METHOD_LABELS[low]) return PAYMENT_METHOD_LABELS[low];
  return s;
};

// =====================
// Import Progress — UX komunikat na czas trwania synchronizacji (2026-02)
// =====================
// Endpoint `POST /api/ksef/inbox/import` jest synchroniczny — jedno wywołanie
// zwraca wynik po pełnej sekwencji (pobranie XML z KSeF, parsing, zapis, agent
// re-run). Może trwać kilka minut.
//
// Struktura projektowana pod przyszłą rozbudowę: gdyby backend zaczął emitować
// etapy (np. przez SSE), wystarczy przekazać `phase` do `ImportProgressOverlay`
// i dodać wpis do `IMPORT_PHASE_LABELS`. Obecnie w UI dostępny tylko fallback
// „Synchronizacja z KSeF..." (zgodnie z wymaganiem).
const IMPORT_PHASE_LABELS = {
  connecting: "Łączenie z KSeF...",
  fetching_list: "Pobieranie listy faktur...",
  fetching_xml: "Pobieranie dokumentów XML...",
  analyzing: "Analiza dokumentów...",
  saving: "Zapisywanie faktur...",
  updating_vendors: "Aktualizacja kontrahentów...",
  finishing: "Kończenie synchronizacji...",
};

const ImportProgressOverlay = ({ phase = null, processed = null, total = null }) => {
  const heading = (phase && IMPORT_PHASE_LABELS[phase]) || "Synchronizacja z KSeF...";
  const showProgress = processed != null && total != null && total > 0;
  return (
    <div
      className="mt-3 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-3"
      data-testid="inbox-import-progress"
      role="status"
      aria-live="polite"
    >
      <Loader2 className="w-5 h-5 text-blue-600 animate-spin shrink-0 mt-0.5" data-testid="inbox-import-spinner" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-blue-900" data-testid="inbox-import-heading">
          {heading}
        </div>
        <div className="text-xs text-blue-800 mt-0.5" data-testid="inbox-import-subtext">
          {showProgress
            ? `Przetworzono ${processed} z ${total} faktur.`
            : "Trwa pobieranie szczegółowych danych z KSeF. Może to potrwać kilka minut."}
        </div>
      </div>
    </div>
  );
};


const DetailItem = ({ label, value, mono }) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-gray-900 ${mono ? "font-mono text-xs" : ""}`}>{value}</div>
  </div>
);

const SummaryLine = ({ label, value, bold }) => (
  <div className={`flex justify-between ${bold ? "text-base font-semibold border-t border-gray-200 pt-2" : "text-gray-700"}`}>
    <span>{label}</span>
    <span className="font-mono">{value}</span>
  </div>
);

// =====================
// Status badge — Zaimportowana / Wykluczona / Do importu
// =====================
const StatusBadge = ({ isImported, isExcluded }) => {
  if (isImported) {
    return (
      <Badge variant="secondary" className="bg-green-100 text-green-800 whitespace-nowrap">
        <Check className="w-3 h-3 mr-1" /> Zaimportowana
      </Badge>
    );
  }
  if (isExcluded) {
    return (
      <Badge variant="outline" className="text-gray-500 border-gray-300 bg-gray-50 whitespace-nowrap">
        <Ban className="w-3 h-3 mr-1" /> Wykluczona
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 whitespace-nowrap">
      Do importu
    </Badge>
  );
};

// =====================
// 3-dots row menu — Wyklucz / Usuń / Przywróć (kontekstowe)
// =====================
const InboxRowMenu = ({ inv, onExclude, onDelete, onRestore }) => {
  const canExclude = !inv.imported_to_system && !inv.excluded;
  const canRestore = inv.excluded;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600 hover:text-gray-900"
          onClick={(e) => e.stopPropagation()}
          data-testid={`inbox-row-menu-${inv.ksef_number}`}
          aria-label="Akcje faktury"
        >
          <MoreVertical className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {canExclude && (
          <DropdownMenuItem
            onClick={onExclude}
            data-testid={`inbox-action-exclude-${inv.ksef_number}`}
            className="text-amber-700 focus:text-amber-800 focus:bg-amber-50"
          >
            <Ban className="w-4 h-4 mr-2" />
            Wyklucz
          </DropdownMenuItem>
        )}
        {canRestore && (
          <DropdownMenuItem
            onClick={onRestore}
            data-testid={`inbox-action-restore-${inv.ksef_number}`}
            className="text-blue-700 focus:text-blue-800 focus:bg-blue-50"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Przywróć
          </DropdownMenuItem>
        )}
        {(canExclude || canRestore) && <DropdownMenuSeparator />}
        <DropdownMenuItem
          onClick={onDelete}
          data-testid={`inbox-action-delete-${inv.ksef_number}`}
          className="text-red-700 focus:text-red-800 focus:bg-red-50"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Usuń
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// =====================
// Mobile card — kompaktowy układ faktury KSeF
// =====================
const InboxMobileCard = ({ inv, isSelected, onToggle, onPreview, onExclude, onDelete, onRestore }) => {
  const dimmed = inv.imported_to_system || inv.excluded;
  return (
    <Card
      className={`p-3 ${dimmed ? "opacity-70" : ""}`}
      data-testid={`inbox-card-${inv.ksef_number}`}
    >
      {/* Top row: checkbox + status badge + menu */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isSelected}
            onCheckedChange={onToggle}
            data-testid={`inbox-card-select-${inv.ksef_number}`}
            aria-label={`Zaznacz ${inv.invoice_number}`}
          />
          <StatusBadge isImported={inv.imported_to_system} isExcluded={inv.excluded} />
        </div>
        <InboxRowMenu inv={inv} onExclude={onExclude} onDelete={onDelete} onRestore={onRestore} />
      </div>

      {/* Body — klikalny dla preview */}
      <button
        type="button"
        onClick={onPreview}
        className="w-full text-left"
        data-testid={`inbox-card-body-${inv.ksef_number}`}
      >
        <div className="font-mono text-sm font-semibold text-gray-900 break-all">
          {inv.invoice_number || "—"}
        </div>
        <div className="text-sm text-gray-700 mt-1 line-clamp-1" data-testid={`inbox-card-seller-${inv.ksef_number}`}>
          {inv.seller_name || "—"}
        </div>
        <div className="text-xs text-gray-500 mt-0.5">NIP: {inv.seller_nip || "—"}</div>

        <div className="flex items-end justify-between mt-2 pt-2 border-t border-gray-100">
          <div className="text-xs text-gray-500">
            <div>{formatMonth(inv.invoice_date)}</div>
            <div className="mt-0.5">Otrz.: {formatDate(inv.ksef_received_at)}</div>
          </div>
          <div className="font-mono text-base font-semibold text-gray-900 whitespace-nowrap" data-testid={`inbox-card-amount-${inv.ksef_number}`}>
            {formatMoney(inv.gross_amount, inv.currency)}
          </div>
        </div>
      </button>
    </Card>
  );
};

// =====================
// Main page
// =====================
const KsefInboxPage = () => {
  // Pakiet 1: KSeF Inbox to strona gęsta danymi — używamy szerszego kontenera.
  usePageContainer("wide");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ total: 0, imported: 0, not_imported: 0, excluded: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all|imported|not_imported|excluded
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);
  // 2026-02 UX polish — pola pod przyszłe przekazywanie postępu z backendu
  // (np. przez SSE). Obecnie backend zwraca wynik synchronicznie po całości,
  // więc te stany są zawsze `null` — komponent `ImportProgressOverlay`
  // wtedy pokazuje fallback „Synchronizacja z KSeF...".
  const [importPhase, setImportPhase] = useState(null);
  const [importProcessed, setImportProcessed] = useState(null);
  const [importTotal, setImportTotal] = useState(null);
  const [syncing, setSyncing] = useState(false);
  // Round 21: usunięty lokalny `previewKsefNumber` state — używamy
  // globalnego contextu (openInvoice / closeInvoice).
  // Round 21: JEDYNY globalny mechanizm podglądu — context (jeden Dialog w MainLayout).
  const { openInvoice, closeInvoice } = useInvoicePreview();
  const [searchInput, setSearchInput] = useState("");
  // Modal wyboru zakresu pobierania z KSeF
  const [pullDialogOpen, setPullDialogOpen] = useState(false);
  const [pullPreset, setPullPreset] = useState("30d");
  const [pullDateFrom, setPullDateFrom] = useState("");
  const [pullDateTo, setPullDateTo] = useState("");
  // Confirm modals dla Wyklucz / Usuń
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'exclude'|'delete', invoice }
  const [actionInFlight, setActionInFlight] = useState(false);
  // Daily pull jobs dialog
  const [dailyPullOpen, setDailyPullOpen] = useState(false);

  const fetchInbox = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: "0", limit: "200" });
      if (search) params.append("search", search);
      if (statusFilter !== "all") params.append("imported_status", statusFilter);
      const res = await axios.get(`${API}/ksef/inbox?${params.toString()}`, { withCredentials: true });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setCounts(res.data.counts || { total: 0, imported: 0, not_imported: 0, excluded: 0 });
      setSelected(new Set());
    } catch (e) {
      logger.error("Inbox fetch error", e);
      toast.error("Nie udało się pobrać inboxu KSeF");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  // eslint-disable-next-line
  useEffect(() => { fetchInbox(); }, [fetchInbox]);

  const handlePullFromKsef = async () => {
    const body = { invoice_type: "purchase" };
    if (pullPreset === "custom") {
      if (!pullDateFrom || !pullDateTo) {
        toast.error("Wybierz datę OD i DO");
        return;
      }
      if (pullDateFrom > pullDateTo) {
        toast.error("Data OD nie może być późniejsza niż DO");
        return;
      }
      body.date_from = pullDateFrom;
      body.date_to = pullDateTo;
    } else {
      body.preset = pullPreset;
    }

    try {
      setSyncing(true);
      setPullDialogOpen(false);
      const res = await axios.post(
        `${API}/ksef/invoices/pull`,
        body,
        { withCredentials: true }
      );
      if (res.data.ok) {
        const newCount = res.data.new || 0;
        const totalFound = res.data.total || 0;
        if (newCount > 0) {
          toast.success(`Pobrano ${newCount} nowych faktur z KSeF`);
        } else if (totalFound > 0) {
          toast.info(`Wszystkie ${totalFound} faktury już są w inboxie`);
        } else {
          toast.info("Brak nowych faktur w KSeF dla ostatnich 30 dni");
        }
        fetchInbox();
      } else {
        toast.error(res.data.message || "Błąd pobierania z KSeF");
      }
    } catch (e) {
      logger.error("Pull error", e);
      toast.error(e.response?.data?.detail || "Błąd połączenia z KSeF");
    } finally {
      setSyncing(false);
    }
  };

  const handleImportSelected = async (ksefNumbersOverride) => {
    const ids = ksefNumbersOverride || Array.from(selected);
    if (ids.length === 0) {
      toast.error("Zaznacz co najmniej jedną fakturę do importu");
      return;
    }
    try {
      setImporting(true);
      setImportPhase(null);
      setImportProcessed(null);
      setImportTotal(ids.length);
      const res = await axios.post(
        `${API}/ksef/inbox/import`,
        { invoice_ids: ids },
        { withCredentials: true }
      );
      const { imported, skipped, not_found, agent_rerun } = res.data;
      const parts = [];
      if (imported > 0) parts.push(`zaimportowano ${imported}`);
      if (skipped > 0) parts.push(`pominięto ${skipped} (już istniejące)`);
      if (not_found > 0) parts.push(`nie znaleziono ${not_found}`);
      toast.success(parts.length > 0 ? parts.join(", ") : "Brak zmian");
      // Incremental Agent Faktur re-run (addytywne, informacyjne).
      const ia = agent_rerun?.invoice_agent;
      if (ia?.triggered) {
        const agentParts = [];
        if (ia.analyzed > 0) agentParts.push(`zaktualizowane profile: ${ia.analyzed}`);
        if (ia.cached > 0) agentParts.push(`z cache: ${ia.cached}`);
        if (ia.failed > 0) agentParts.push(`błędy: ${ia.failed}`);
        if (agentParts.length > 0) {
          toast.info(`Agent Faktur — ${agentParts.join(", ")}`, {
            description: ia.vendor_nips?.length ? `NIP: ${ia.vendor_nips.join(", ")}` : undefined,
          });
        }
      }
      // Round 21: modal zamykamy przez globalny context (closeInvoice), nie
      // przez lokalny state — po importzie modal zostaje zamknięty przez
      // callback w extraFooterAction.
      closeInvoice();
      fetchInbox();
    } catch (e) {
      logger.error("Import error", e);
      toast.error(e.response?.data?.detail || "Błąd importu do systemu");
    } finally {
      setImporting(false);
      setImportPhase(null);
      setImportProcessed(null);
      setImportTotal(null);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const handleExclude = async (inv) => {
    try {
      setActionInFlight(true);
      await axios.post(
        `${API}/ksef/inbox/${encodeURIComponent(inv.ksef_number)}/exclude`,
        {},
        { withCredentials: true }
      );
      toast.success(`Wykluczono fakturę ${inv.invoice_number || inv.ksef_number}`);
      setConfirmAction(null);
      fetchInbox();
    } catch (e) {
      logger.error("Exclude error", e);
      toast.error(e.response?.data?.detail || "Nie udało się wykluczyć faktury");
    } finally {
      setActionInFlight(false);
    }
  };

  const handleRestore = async (inv) => {
    try {
      await axios.post(
        `${API}/ksef/inbox/${encodeURIComponent(inv.ksef_number)}/restore`,
        {},
        { withCredentials: true }
      );
      toast.success(`Przywrócono fakturę ${inv.invoice_number || inv.ksef_number}`);
      fetchInbox();
    } catch (e) {
      logger.error("Restore error", e);
      toast.error(e.response?.data?.detail || "Nie udało się przywrócić faktury");
    }
  };

  const handleDelete = async (inv) => {
    try {
      setActionInFlight(true);
      const res = await axios.delete(
        `${API}/ksef/inbox/${encodeURIComponent(inv.ksef_number)}`,
        { withCredentials: true }
      );
      const { deleted_pax, was_imported } = res.data;
      if (was_imported && deleted_pax > 0) {
        toast.success("Faktura usunięta z inboxu i systemu PAX");
      } else if (was_imported && deleted_pax === 0) {
        toast.warning("Faktura usunięta z inboxu. Rekord w systemie PAX nie został znaleziony.");
      } else {
        toast.success("Faktura usunięta z inboxu");
      }
      setConfirmAction(null);
      // Wyczyść z zaznaczonych jeśli była
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(inv.ksef_number);
        return next;
      });
      fetchInbox();
    } catch (e) {
      logger.error("Delete error", e);
      toast.error(e.response?.data?.detail || "Nie udało się usunąć faktury");
    } finally {
      setActionInFlight(false);
    }
  };

  const importableSelected = useMemo(
    () => Array.from(selected).filter((id) => {
      const inv = items.find((i) => i.ksef_number === id);
      return inv && !inv.imported_to_system;
    }),
    [selected, items]
  );

  // Główny przycisk "Importuj":
  //  - są zaznaczone (importowalne) → import zaznaczonych
  //  - brak zaznaczenia + są nowe (not_imported) → import wszystkich nowych z bieżącej listy
  //  - brak nowych → disabled + komunikat
  const hasSelection = importableSelected.length > 0;
  const newCount = counts.not_imported;
  const importBtnDisabled = importing || (!hasSelection && newCount === 0);
  // Label przycisku podczas trwającego importu — zgodnie z UX guideline:
  // preferowany duży komunikat pojawia się w `ImportProgressOverlay` pod
  // paskiem akcji. Sam przycisk pokazuje krótki, jednoznaczny tekst.
  const importBtnLabel = importing
    ? "Synchronizacja z KSeF..."
    : hasSelection
      ? `Zaimportuj ${importableSelected.length} ${importableSelected.length === 1 ? "zaznaczoną fakturę" : "zaznaczonych faktur"}`
      : newCount === 0
        ? "Brak nowych faktur do importu"
        : `Zaimportuj ${newCount} ${newCount === 1 ? "nową fakturę" : "nowych faktur"}`;

  const onMainImportClick = () => {
    if (hasSelection) {
      handleImportSelected();
      return;
    }
    const allNew = items
      .filter((i) => !i.imported_to_system && !i.excluded)
      .map((i) => i.ksef_number);
    if (allNew.length > 0) handleImportSelected(allNew);
  };

  const allVisibleSelected = items.length > 0 && items.every((i) => selected.has(i.ksef_number));
  const someVisibleSelected = items.some((i) => selected.has(i.ksef_number));

  const toggleAll = () => {
    if (allVisibleSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map((i) => i.ksef_number)));
    }
  };

  const toggleOne = (ksefNumber) => {
    const next = new Set(selected);
    if (next.has(ksefNumber)) next.delete(ksefNumber);
    else next.add(ksefNumber);
    setSelected(next);
  };

  const previewKsefInbox = useCallback((inv) => {
    if (!inv?.ksef_number) return;
    openInvoice(inv, {
      extraFooterAction: !inv.imported_to_system ? {
        label: importing ? "Importuję…" : "Importuj do systemu",
        onClick: async () => { await handleImportSelected([inv.ksef_number]); closeInvoice(); },
        disabled: importing,
        testId: "inbox-preview-import-btn",
        className: "bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-70",
      } : null,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openInvoice, closeInvoice, importing]);

  return (
    <div data-testid="ksef-inbox-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Inbox className="w-7 h-7 text-blue-600" />
          KSeF Inbox
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Faktury pobrane z Krajowego Systemu e-Faktur. Wybierz dokumenty i zaimportuj do systemu PAX.
        </p>
      </div>

      {/* Auto-sync — checkbox + status */}
      <div className="mb-4">
        <AutoSyncCard onNewInvoices={fetchInbox} />
      </div>

      {/* Toolbar — pionowy na mobile, poziomy na desktop */}
      <Card className="p-4 mb-4">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          {/* Lewa kolumna: status połączenia + podsumowanie liczbowe */}
          <div className="flex flex-col gap-2 text-sm" data-testid="inbox-summary">
            <div className="flex items-center gap-2 text-gray-700">
              <Cloud className="w-4 h-4 text-green-600 shrink-0" />
              <span>Połączono z KSeF</span>
            </div>
            <dl className="grid grid-cols-[auto_auto] gap-x-3 gap-y-1 text-sm" aria-label="Podsumowanie inboxu">
              <dt className="text-gray-600">W KSeF:</dt>
              <dd className="text-gray-900">
                <span className="font-semibold" data-testid="inbox-total-count">{counts.total}</span>{" "}
                <span className="text-gray-500">faktur</span>
              </dd>
              <dt className="text-gray-600">Zaimportowano:</dt>
              <dd className="text-gray-900">
                <span className="font-semibold" data-testid="inbox-imported-count">{counts.imported}</span>{" "}
                <span className="text-gray-500">faktur</span>
              </dd>
              <dt className="text-gray-600">Nowych:</dt>
              <dd className="text-gray-900">
                <span className="font-semibold text-blue-700" data-testid="inbox-new-count">{counts.not_imported}</span>{" "}
                <span className="text-gray-500">faktur</span>
              </dd>
            </dl>
          </div>
          <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchInbox}
              disabled={loading}
              data-testid="inbox-refresh-btn"
              className="w-full md:w-auto"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Odśwież
            </Button>
            <Button
              size="sm"
              onClick={() => setPullDialogOpen(true)}
              disabled={syncing}
              data-testid="inbox-pull-btn"
              className="bg-blue-600 hover:bg-blue-700 w-full md:w-auto"
            >
              <Download className={`w-4 h-4 mr-2 ${syncing ? "animate-pulse" : ""}`} />
              {syncing ? "Pobieranie…" : "Pobierz z KSeF"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDailyPullOpen(true)}
              data-testid="inbox-daily-pull-btn"
              className="w-full md:w-auto border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              <CalendarRange className="w-4 h-4 mr-2" />
              Kolejka dzienna
            </Button>
            <Button
              size="sm"
              onClick={onMainImportClick}
              disabled={importBtnDisabled}
              data-testid="inbox-import-btn"
              className="bg-green-600 hover:bg-green-700 disabled:opacity-70 w-full md:w-auto"
              aria-busy={importing}
            >
              {importing
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" data-testid="inbox-import-btn-spinner" />
                : <FileText className="w-4 h-4 mr-2" />}
              {importBtnLabel}
            </Button>
          </div>
        </div>
        {importing && (
          <ImportProgressOverlay
            phase={importPhase}
            processed={importProcessed}
            total={importTotal}
          />
        )}
      </Card>

      {/* Filters — pionowo na mobile */}
      <Card className="p-4 mb-4">
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <form onSubmit={handleSearchSubmit} className="flex-1 flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Szukaj po numerze, NIP lub nazwie sprzedawcy…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-8"
                data-testid="inbox-search-input"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => { setSearchInput(""); setSearch(""); }}
                  className="absolute right-2 top-2.5"
                  data-testid="inbox-search-clear-btn"
                >
                  <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
            <Button type="submit" variant="outline" size="sm" data-testid="inbox-search-submit-btn" className="w-full sm:w-auto">Szukaj</Button>
          </form>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-48" data-testid="inbox-status-filter">
              <SelectValue placeholder="Status importu" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Wszystkie ({counts.total - counts.excluded})</SelectItem>
              <SelectItem value="not_imported">Do importu ({counts.not_imported})</SelectItem>
              <SelectItem value="imported">Zaimportowane ({counts.imported})</SelectItem>
              <SelectItem value="excluded">Wykluczone ({counts.excluded})</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Mobile: karty (md:hidden), Desktop: tabela (hidden md:block) */}
      <div className="md:hidden space-y-2" data-testid="inbox-mobile-list">
        {loading ? (
          <Card className="p-8 text-center text-gray-500" data-testid="inbox-mobile-loading">Wczytywanie inboxu…</Card>
        ) : items.length === 0 ? (
          <Card className="p-8 text-center text-gray-500" data-testid="inbox-mobile-empty">
            Brak faktur w inboxie. Kliknij „Pobierz z KSeF&rdquo;.
          </Card>
        ) : (
          <>
            {/* Mobile master select bar — odpowiednik checkboxa w thead na desktop */}
            <Card className="p-3 flex items-center gap-3" data-testid="inbox-mobile-select-all-bar">
              <Checkbox
                checked={allVisibleSelected ? true : (someVisibleSelected ? "indeterminate" : false)}
                onCheckedChange={toggleAll}
                data-testid="inbox-mobile-select-all"
                aria-label="Zaznacz wszystkie widoczne faktury"
              />
              <button
                type="button"
                onClick={toggleAll}
                className="text-sm text-gray-700 hover:text-gray-900 flex-1 text-left"
                data-testid="inbox-mobile-select-all-label"
              >
                {allVisibleSelected
                  ? `Odznacz wszystkie (${items.length})`
                  : `Zaznacz wszystkie (${items.length})`}
              </button>
            </Card>

            {items.map((inv) => (
              <InboxMobileCard
                key={inv.ksef_number}
                inv={inv}
                isSelected={selected.has(inv.ksef_number)}
                onToggle={() => toggleOne(inv.ksef_number)}
                onPreview={() => previewKsefInbox(inv)}
                onExclude={() => setConfirmAction({ type: "exclude", invoice: inv })}
                onDelete={() => setConfirmAction({ type: "delete", invoice: inv })}
                onRestore={() => handleRestore(inv)}
              />
            ))}
          </>
        )}
      </div>

      {/* Table — pełna na desktop. Pakiet 1: bez `overflow-x-auto` — 
          poboczne kolumny są chowane progresywnie (md/lg/xl:table-cell),
          kluczowe (nr, sprzedawca, kwota, status, akcje) są zawsze widoczne. */}
      <Card className="hidden md:block">
        <div>
          <table className="w-full text-sm table-responsive" data-testid="inbox-table">
            <thead className="bg-gray-50 border-b border-gray-200 text-gray-700">
              <tr>
                <th className="col-checkbox px-3 py-2 align-middle">
                  <Checkbox
                    checked={allVisibleSelected ? true : (someVisibleSelected ? "indeterminate" : false)}
                    onCheckedChange={toggleAll}
                    data-testid="inbox-select-all"
                    aria-label="Zaznacz wszystkie"
                  />
                </th>
                <th className="text-left px-3 py-3 font-medium">Numer faktury</th>
                <th className="text-left px-3 py-3 font-medium">Sprzedawca</th>
                <th className="text-left px-3 py-3 font-medium hidden lg:table-cell">Miesiąc księgowy</th>
                <th className="text-left px-3 py-3 font-medium hidden xl:table-cell">Źródło</th>
                <th className="text-left px-3 py-3 font-medium hidden lg:table-cell">Data w KSeF</th>
                <th className="text-right px-3 py-3 font-medium">Kwota brutto</th>
                <th className="text-left px-3 py-3 font-medium hidden xl:table-cell">Rachunek bankowy</th>
                <th className="text-left px-3 py-3 font-medium hidden xl:table-cell">Data zapłaty</th>
                <th className="text-left px-3 py-3 font-medium hidden lg:table-cell">Termin płatności</th>
                <th className="text-left px-3 py-3 font-medium">Status importu</th>
                <th className="col-actions px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={12} className="px-3 py-12 text-center text-gray-500" data-testid="inbox-loading">
                    Wczytywanie inboxu…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-3 py-12 text-center text-gray-500" data-testid="inbox-empty">
                    Brak faktur w inboxie. Kliknij „Pobierz z KSeF&rdquo;, aby pobrać dokumenty.
                  </td>
                </tr>
              ) : (
                items.map((inv) => {
                  const isImported = inv.imported_to_system;
                  const isExcluded = inv.excluded;
                  const isSelected = selected.has(inv.ksef_number);
                  return (
                    <tr
                      key={inv.ksef_number}
                      onClick={() => previewKsefInbox(inv)}
                      className={`border-b border-gray-100 hover:bg-blue-50 cursor-pointer transition-colors ${isImported || isExcluded ? "opacity-60" : ""}`}
                      data-testid={`inbox-row-${inv.ksef_number}`}
                    >
                      <td className="col-checkbox px-3 py-3 align-middle" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleOne(inv.ksef_number)}
                          data-testid={`inbox-select-${inv.ksef_number}`}
                          aria-label={`Zaznacz ${inv.invoice_number}`}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <div className="font-mono text-xs text-gray-900 break-all" title={inv.invoice_number || ""}>{inv.invoice_number || "—"}</div>
                      </td>
                      <td
                        className="px-3 py-3"
                        data-testid={`inbox-row-seller-desktop-${inv.ksef_number}`}
                      >
                        <div className="text-gray-900 truncate" title={inv.seller_name || ""}>
                          {inv.seller_name || "—"}
                        </div>
                        <div className="text-xs font-mono text-gray-500 mt-0.5">
                          NIP {inv.seller_nip || "—"}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-gray-700 hidden lg:table-cell">{formatMonth(inv.invoice_date)}</td>
                      <td className="px-3 py-3 hidden xl:table-cell"><Badge variant="outline" className="text-xs">KSeF</Badge></td>
                      <td className="px-3 py-3 text-gray-700 hidden lg:table-cell">{formatDate(inv.ksef_received_at)}</td>
                      <td className="px-3 py-3 text-right font-mono whitespace-nowrap">{formatMoney(inv.gross_amount, inv.currency)}</td>
                      <td className="px-3 py-3 font-mono text-xs text-gray-600 hidden xl:table-cell break-all">{inv.bank_account || "—"}</td>
                      <td className="px-3 py-3 text-gray-700 hidden xl:table-cell">{formatDate(inv.payment_date)}</td>
                      <td className="px-3 py-3 text-gray-700 hidden lg:table-cell">{formatDate(inv.due_date)}</td>
                      <td className="px-3 py-3">
                        <StatusBadge isImported={isImported} isExcluded={isExcluded} />
                      </td>
                      <td className="col-actions px-2 py-3 align-middle" onClick={(e) => e.stopPropagation()}>
                        <InboxRowMenu
                          inv={inv}
                          onExclude={() => setConfirmAction({ type: "exclude", invoice: inv })}
                          onDelete={() => setConfirmAction({ type: "delete", invoice: inv })}
                          onRestore={() => handleRestore(inv)}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Round 21: podgląd faktury renderowany globalnie w
          `InvoicePreviewProvider` (MainLayout). Otwierany przez
          `previewKsefInbox(inv)` z `extraFooterAction=Importuj`. */}

      {/* Modal — wybór zakresu pobierania z KSeF */}
      <Dialog open={pullDialogOpen} onOpenChange={setPullDialogOpen}>
        <DialogContent className="max-w-md" data-testid="inbox-pull-dialog">
          <DialogHeader>
            <DialogTitle>Pobierz faktury z KSeF</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Zakres</label>
              <Select value={pullPreset} onValueChange={setPullPreset}>
                <SelectTrigger data-testid="inbox-pull-preset">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">Ostatnie 7 dni</SelectItem>
                  <SelectItem value="30d">Ostatnie 30 dni</SelectItem>
                  <SelectItem value="90d">Ostatnie 90 dni</SelectItem>
                  <SelectItem value="custom">Własny zakres dat</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {pullPreset === "custom" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Data OD</label>
                  <Input
                    type="date"
                    value={pullDateFrom}
                    onChange={(e) => setPullDateFrom(e.target.value)}
                    data-testid="inbox-pull-date-from"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Data DO</label>
                  <Input
                    type="date"
                    value={pullDateTo}
                    onChange={(e) => setPullDateTo(e.target.value)}
                    data-testid="inbox-pull-date-to"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-2 flex-col-reverse sm:flex-row">
            <Button
              variant="outline"
              onClick={() => setPullDialogOpen(false)}
              data-testid="inbox-pull-cancel-btn"
              className="w-full sm:w-auto"
            >
              Anuluj
            </Button>
            <Button
              onClick={handlePullFromKsef}
              disabled={syncing}
              data-testid="inbox-pull-confirm-btn"
              className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
            >
              <Download className={`w-4 h-4 mr-2 ${syncing ? "animate-pulse" : ""}`} />
              {syncing ? "Pobieranie…" : "Pobierz"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Confirm dialog — Wyklucz / Usuń */}
      <DailyPullJobsPanel
        open={dailyPullOpen}
        onOpenChange={setDailyPullOpen}
        onJobChanged={fetchInbox}
      />

      <AlertDialog
        open={!!confirmAction}
        onOpenChange={(open) => { if (!open && !actionInFlight) setConfirmAction(null); }}
      >
        <AlertDialogContent data-testid="inbox-confirm-dialog">
          {confirmAction?.type === "exclude" && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Wykluczyć fakturę z importu?</AlertDialogTitle>
                <AlertDialogDescription>
                  Faktura <span className="font-mono font-semibold">{confirmAction.invoice.invoice_number || confirmAction.invoice.ksef_number}</span> od&nbsp;
                  <span className="font-semibold">{confirmAction.invoice.seller_name || "—"}</span> zostanie oznaczona jako wykluczona.
                  Nie pojawi się w liście „Do importu&rdquo; i nie zostanie zaimportowana do systemu PAX.
                  Możesz ją w każdej chwili przywrócić z filtra „Wykluczone&rdquo;.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={actionInFlight} data-testid="inbox-confirm-cancel-btn">Anuluj</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => { e.preventDefault(); handleExclude(confirmAction.invoice); }}
                  disabled={actionInFlight}
                  data-testid="inbox-confirm-exclude-btn"
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {actionInFlight ? "Wykluczam…" : "Tak, wyklucz"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
          {confirmAction?.type === "delete" && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Usunąć fakturę z aplikacji?</AlertDialogTitle>
                <AlertDialogDescription>
                  Faktura <span className="font-mono font-semibold">{confirmAction.invoice.invoice_number || confirmAction.invoice.ksef_number}</span> od&nbsp;
                  <span className="font-semibold">{confirmAction.invoice.seller_name || "—"}</span> zostanie trwale usunięta z KSeF Inbox
                  {confirmAction.invoice.imported_to_system && " oraz z listy Faktury (system PAX)"}.
                  Tej operacji nie można cofnąć.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={actionInFlight} data-testid="inbox-confirm-cancel-btn">Anuluj</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => { e.preventDefault(); handleDelete(confirmAction.invoice); }}
                  disabled={actionInFlight}
                  data-testid="inbox-confirm-delete-btn"
                  className="bg-red-600 hover:bg-red-700"
                >
                  {actionInFlight ? "Usuwam…" : "Tak, usuń"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default KsefInboxPage;
